import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { FaWhatsapp, FaCheck, FaLeaf, FaRecycle, FaSeedling } from 'react-icons/fa';

const products = [
  {
    num: '01',
    tag: 'Tarima de Tacón',
    color: '#1F5D8C',
    desc: 'Construida en tres niveles: tablas superiores, tacones (bloques cúbicos de madera) y tablas inferiores. Los tacones elevan la plataforma y crean los canales para la entrada de montacargas por los cuatro costados.',
    features: [
      'Acceso por los 4 lados',
      'Alta rigidez estructural',
      'Apta para cargas estáticas y dinámicas altas',
      'Medidas estándar o a la especificación del cliente',
    ],
    img: '/img/fabrica/tacon.jpg',
    imgFallback: '/img/fabrica/tacon.jpg',
  },
  {
    num: '02',
    tag: 'Polín / Rifle',
    color: '#D0021B',
    desc: 'Fabricada con largueros longitudinales (polines o patines) que recorren el largo de la tarima de extremo a extremo. Diseño continuo, más ligero que la tarima de tacón, con resaque inferior para la entrada de montacargas.',
    features: [
      'Entrada por 2 lados (paralelos a los largueros)',
      'Menor peso, fácil manejo manual',
      'Largueros con resaque para montacargas',
      'Separación y medidas a especificación',
    ],
    img: '/img/fabrica/polin.jpg',
    imgFallback: '/img/fabrica/polin.jpg',
  },
];

const woodTypes = [
  {
    key: 'nueva',
    icon: FaSeedling,
    title: 'Madera Nueva',
    color: '#2e7d32',
    desc: 'Madera de primera calidad, sin uso previo. Máxima resistencia estructural, ideal para clientes que requieren capacidad de carga alta, certificación o exportación.',
  },
  {
    key: 'reciclada',
    icon: FaRecycle,
    title: 'Madera Reciclada',
    color: '#5a8f58',
    desc: 'Madera recuperada, seleccionada y revisada pieza por pieza. Libre de clavos y en buen estado estructural. La opción más económica sin sacrificar funcionalidad.',
  },
  {
    key: 'mixta',
    icon: FaLeaf,
    title: 'Madera Mixta',
    color: '#1F5D8C',
    desc: 'Combina madera nueva en los elementos estructurales principales con tablas recicladas seleccionadas. Balance óptimo entre costo y resistencia.',
  },
];

function FadeUp({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function Products() {
  return (
    <Section id="products">
      <Inner>

        {/* ── Encabezado ── */}
        <FadeUp>
          <SectionTag>Nuestros Productos</SectionTag>
          <SectionTitle>Dos tipos de <AccentWord>tarima</AccentWord></SectionTitle>
          <SectionLead>
            Fabricamos tarimas de tacón y polín/rifle bajo las especificaciones
            técnicas de cada cliente. Trabajamos con madera nueva, reciclada y mixta
            para adaptarnos a cada necesidad y presupuesto.
          </SectionLead>
        </FadeUp>

        {/* ── Tarimas ── */}
        <ProductGrid>
          {products.map((p, i) => (
            <motion.div
              key={p.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              <ProductCard $flip={i % 2 !== 0}>

                {/* Visual */}
                <CardVisual $color={p.color}>
                  <CardTopBar $color={p.color} />
                  <CardNumBg>{p.num}</CardNumBg>
                  <CardImgWrap>
                    <img
                      src={p.imgFallback}
                      alt={p.tag}
                      onError={e => { e.target.src = p.imgFallback; }}
                    />
                  </CardImgWrap>
                  <CardLineBadge $color={p.color}>{p.tag}</CardLineBadge>
                </CardVisual>

                {/* Info */}
                <CardInfo>
                  <CardName $color={p.color}>{p.tag}</CardName>
                  <CardDesc>{p.desc}</CardDesc>

                  <FeatureList>
                    {p.features.map((f, fi) => (
                      <FeatureItem key={fi} $color={p.color}>
                        <FaCheck size={10} />
                        <span>{f}</span>
                      </FeatureItem>
                    ))}
                  </FeatureList>

                  <CardActions>
                    <CardCotizar href="https://wa.me/524311011283?text=Hola%2C%20los%20contacto%20desde%20su%20sitio%20web." target="_blank" rel="noopener">
                      <FaWhatsapp size={15} /> Cotizar
                    </CardCotizar>
                  </CardActions>
                </CardInfo>

              </ProductCard>
            </motion.div>
          ))}
        </ProductGrid>

        {/* ── Tipos de madera ── */}
        <FadeUp>
          <WoodSectionHead>
            <WoodTag>Material</WoodTag>
            <WoodTitle>Trabajamos con <AccentWord>tres tipos</AccentWord> de madera</WoodTitle>
          </WoodSectionHead>
        </FadeUp>

        <WoodGrid>
          {woodTypes.map((w, i) => {
            const Icon = w.icon;
            return (
              <motion.div
                key={w.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <WoodCard $color={w.color}>
                  <WoodIconWrap $color={w.color}>
                    <Icon size={22} />
                  </WoodIconWrap>
                  <WoodCardTitle $color={w.color}>{w.title}</WoodCardTitle>
                  <WoodCardDesc>{w.desc}</WoodCardDesc>
                </WoodCard>
              </motion.div>
            );
          })}
        </WoodGrid>

        {/* ── Paneles de características ── */}
        <FeaturePanels>
          <FadeUp>
            <FeaturePanel>
              <PanelEyebrow>
                Fabricación a medida
              </PanelEyebrow>
              <PanelHeadline>
                ¿Necesitas<br />una medida<br /><em>diferente?</em>
              </PanelHeadline>
              <PanelText>
                Las fabricamos. Adaptamos dimensiones, espesor de tablas, separación de largueros
                y tipo de madera a las especificaciones técnicas que tu operación requiera.
              </PanelText>
              <PanelLink href="https://wa.me/524311011283?text=Hola%2C%20los%20contacto%20desde%20su%20sitio%20web." target="_blank" rel="noopener">
                <FaWhatsapp size={15} />
                Solicitar cotización
              </PanelLink>
            </FeaturePanel>
          </FadeUp>

          <FadeUp delay={0.15}>
            <FeaturePanelDark>
              <PanelEyebrow $dark>
                <FaCheck size={12} /> Entregas a tiempo
              </PanelEyebrow>
              <PanelHeadlineDark>
                Volumen<br />y entrega<br /><em>garantizados</em>
              </PanelHeadlineDark>
              <PanelTextDark>
                Contamos con inventario y capacidad de producción para responder pedidos
                chicos o de gran volumen, con tiempos de entrega definidos y cumplimiento garantizado.
              </PanelTextDark>
            </FeaturePanelDark>
          </FadeUp>
        </FeaturePanels>

      </Inner>
    </Section>
  );
}

/* ── Estilos ── */

const Section = styled.section`
  background: transparent;
  padding: 7rem 2rem;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTag = styled.p`
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--blue);
  border-left: 3px solid var(--red);
  padding-left: 0.75rem;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 3.2rem);
  margin-bottom: 1.25rem;
  color: var(--blue);
`;

const AccentWord = styled.span`
  color: var(--red);
  text-shadow: var(--shadow-3d);
`;

const SectionLead = styled.p`
  max-width: 560px;
  font-size: 0.95rem;
  line-height: 1.8;
  color: var(--text-sub);
  margin-bottom: 4rem;
`;

/* ── Product cards ── */

const ProductGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 5rem;
  border-top: 2px solid var(--blue);
`;

const ProductCard = styled.div`
  display: grid;
  grid-template-columns: ${({ $flip }) => $flip ? '1fr 480px' : '480px 1fr'};
  min-height: 380px;
  border-bottom: 1px solid var(--border);
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: 260px auto;
  }
`;

const CardVisual = styled.div`
  position: relative;
  background: ${({ $color }) => $color}18;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 900px) {
    order: -1;
  }
`;

const CardTopBar = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 4px;
  background: ${({ $color }) => $color};
`;

const CardNumBg = styled.span`
  position: absolute;
  bottom: -1rem;
  right: 1rem;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 9rem;
  line-height: 1;
  color: rgba(0,0,0,0.06);
  user-select: none;
  pointer-events: none;
`;

const CardImgWrap = styled.div`
  width: 100%;
  height: 100%;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s ease;
  }
  &:hover img { transform: scale(1.04); }
`;

const CardLineBadge = styled.span`
  position: absolute;
  top: 1.25rem;
  left: 1.25rem;
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #fff;
  background: ${({ $color }) => $color};
  padding: 0.3rem 0.75rem;
`;

const CardInfo = styled.div`
  padding: 3rem 3.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0;
  @media (max-width: 900px) { padding: 2rem 1.5rem; }
`;

const CardName = styled.h3`
  font-family: 'Bebas Neue', sans-serif;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  color: ${({ $color }) => $color || 'var(--blue)'};
  letter-spacing: 0.03em;
  line-height: 1;
  margin-bottom: 1rem;
`;

const CardDesc = styled.p`
  font-size: 0.9rem;
  line-height: 1.75;
  color: var(--text-sub);
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-bottom: 2rem;
  padding: 0;
  list-style: none;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--text);
  svg {
    color: ${({ $color }) => $color || 'var(--blue)'};
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const CardCotizar = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.4rem;
  background: var(--green-wa);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

/* ── Sección tipos de madera ── */

const WoodSectionHead = styled.div`
  margin-bottom: 2.5rem;
`;

const WoodTag = styled.p`
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--blue);
  border-left: 3px solid var(--red);
  padding-left: 0.75rem;
  margin-bottom: 0.75rem;
`;

const WoodTitle = styled.h2`
  font-size: clamp(1.6rem, 4vw, 2.6rem);
  color: var(--blue);
`;

const WoodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-bottom: 5rem;
  border-top: 2px solid var(--border);
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const WoodCard = styled.div`
  padding: 2.5rem 2rem;
  border-right: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  background: white;
  transition: background 0.2s;
  &:last-child { border-right: none; }
  &:hover { background: ${({ $color }) => $color}08; }
  @media (max-width: 768px) {
    border-right: none;
    border-left: 4px solid ${({ $color }) => $color};
  }
`;

const WoodIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ $color }) => $color}18;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color};
  margin-bottom: 1.25rem;
`;

const WoodCardTitle = styled.h4`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.3rem;
  letter-spacing: 0.04em;
  color: ${({ $color }) => $color};
  margin-bottom: 0.75rem;
`;

const WoodCardDesc = styled.p`
  font-size: 0.88rem;
  line-height: 1.75;
  color: var(--text-sub);
`;

/* ── Paneles ── */

const FeaturePanels = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const FeaturePanel = styled.div`
  padding: 4rem 3.5rem;
  background: var(--white);
  border-left: 4px solid var(--red);
  @media (max-width: 768px) { padding: 3rem 2rem; }
`;

const FeaturePanelDark = styled.div`
  padding: 4rem 3.5rem;
  background: var(--text);
  @media (max-width: 768px) { padding: 3rem 2rem; }
`;

const PanelEyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ $dark }) => $dark ? 'rgba(255,255,255,0.4)' : 'var(--red)'};
  margin-bottom: 1.5rem;
`;

const PanelHeadline = styled.h3`
  font-family: 'Bebas Neue', sans-serif;
  font-size: clamp(2.2rem, 4vw, 3.2rem);
  line-height: 1.05;
  letter-spacing: 0.02em;
  color: var(--text);
  margin-bottom: 1.5rem;
  em {
    font-style: normal;
    color: var(--red);
    text-shadow: var(--shadow-3d);
  }
`;

const PanelHeadlineDark = styled(PanelHeadline)`color: #fff;`;

const PanelText = styled.p`
  font-size: 0.92rem;
  line-height: 1.8;
  color: var(--text-sub);
  max-width: 380px;
  margin-bottom: 2rem;
`;

const PanelTextDark = styled(PanelText)`color: rgba(255,255,255,0.55);`;

const PanelLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--green-wa);
  font-weight: 700;
  font-size: 0.9rem;
  border-bottom: 2px solid rgba(37,211,102,0.3);
  padding-bottom: 0.1rem;
  transition: border-color 0.2s;
  &:hover { border-color: var(--green-wa); }
`;
